import {BACKEND_URL} from "../../../configs";
import {globalFetchConfig} from "../../../api/apiConfiguration";

/**
 * Custom knowledge types and attributes (MTA-1234).
 *
 * Hand written like knowledgeApi.ts: the endpoints are newer than the generated client. Auth goes
 * through globalFetchConfig, so these calls carry the same Clerk token as every other request.
 */

export enum KnowledgeDefinitionKind {
    TYPE = "TYPE",
    ATTRIBUTE = "ATTRIBUTE"
}

/** Built-in types come first with a null publicId and builtIn=true; they cannot be changed. */
export interface KnowledgeTypeDefinitionUi {
    publicId: string | null;
    key: string;
    name: string;
    description: string;
    builtIn: boolean;
}

export interface KnowledgeAttributeDefinitionUi {
    publicId: string;
    key: string;
    name: string;
    description: string;
    /** Values records already hold, sorted. Offered while typing so one value is not spelled several ways. */
    values?: string[];
}

export type KnowledgeDefinitionUi = KnowledgeTypeDefinitionUi | KnowledgeAttributeDefinitionUi;

/** The platform derives the key from the name (on create and on rename), so only these two are sent. */
export interface KnowledgeDefinitionRequest {
    name: string;
    description: string;
}

export const KNOWLEDGE_DEFINITION_NAME_MAX_LENGTH = 100;
export const KNOWLEDGE_DEFINITION_DESCRIPTION_MAX_LENGTH = 1000;
export const KNOWLEDGE_ATTRIBUTE_VALUE_MAX_LENGTH = 255;
/** The platform rejects longer knowledge record content. */
export const KNOWLEDGE_RECORD_CONTENT_MAX_LENGTH = 40000;

/**
 * The key the platform derives from a name (KnowledgeDefinitionRules): runs of anything but ASCII
 * letters and digits become "_", with none at either end. Types are UPPER_SNAKE, attributes
 * lower_snake. Only used to preview the key; the platform's is the one saved.
 */
export const deriveKnowledgeDefinitionKey = (kind: KnowledgeDefinitionKind, name: string): string => {
    const snake = name.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    return kind === KnowledgeDefinitionKind.TYPE ? snake.toUpperCase() : snake.toLowerCase();
};

const DEFINITION_PATHS: Record<KnowledgeDefinitionKind, string> = {
    [KnowledgeDefinitionKind.TYPE]: "knowledge-type",
    [KnowledgeDefinitionKind.ATTRIBUTE]: "knowledge-attribute"
};

const DEFINITION_NAMES: Record<KnowledgeDefinitionKind, string> = {
    [KnowledgeDefinitionKind.TYPE]: "knowledge type",
    [KnowledgeDefinitionKind.ATTRIBUTE]: "attribute"
};

export const getKnowledgeDefinitionName = (kind: KnowledgeDefinitionKind): string => DEFINITION_NAMES[kind];

const definitionUrl = (kind: KnowledgeDefinitionKind, publicId?: string): string => {
    const base = `${BACKEND_URL}/api/private/${DEFINITION_PATHS[kind]}`;
    return publicId ? `${base}/${encodeURIComponent(publicId)}` : base;
};

/**
 * A 400 from the platform carries a plain text message written for the operator, for example
 * "Knowledge type is used by 3 knowledge records". Anything else gets the generic fallback.
 */
export const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    if (response.status !== 400) {
        return fallback;
    }
    try {
        const body = await response.text();
        return body && body.trim() ? body.trim() : fallback;
    } catch {
        return fallback;
    }
};

const fetchDefinitions = async <T, >(kind: KnowledgeDefinitionKind): Promise<T[]> => {
    const response = await globalFetchConfig(definitionUrl(kind), {method: "GET"});
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Failed to load ${DEFINITION_NAMES[kind]}s`));
    }
    return (await response.json()) || [];
};

export const fetchKnowledgeTypeDefinitions = (): Promise<KnowledgeTypeDefinitionUi[]> =>
    fetchDefinitions<KnowledgeTypeDefinitionUi>(KnowledgeDefinitionKind.TYPE);

export const fetchKnowledgeAttributeDefinitions = (): Promise<KnowledgeAttributeDefinitionUi[]> =>
    fetchDefinitions<KnowledgeAttributeDefinitionUi>(KnowledgeDefinitionKind.ATTRIBUTE);

/** Creates the definition when publicId is absent, otherwise updates its name and description. */
export const saveKnowledgeDefinition = async (kind: KnowledgeDefinitionKind,
                                              request: KnowledgeDefinitionRequest,
                                              publicId?: string): Promise<KnowledgeDefinitionUi> => {
    const response = await globalFetchConfig(definitionUrl(kind, publicId), {
        method: publicId ? "PUT" : "POST",
        body: JSON.stringify({name: request.name.trim(), description: request.description.trim()})
    });
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Failed to save the ${DEFINITION_NAMES[kind]}`));
    }
    return await response.json();
};

export const deleteKnowledgeDefinition = async (kind: KnowledgeDefinitionKind, publicId: string): Promise<void> => {
    const response = await globalFetchConfig(definitionUrl(kind, publicId), {method: "DELETE"});
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Failed to delete the ${DEFINITION_NAMES[kind]}. Please try again.`));
    }
};
